// src/components/books/PageResponseModal.jsx - UPDATED
import React, { useState, useRef } from 'react';
import { X, Upload, Image, FileText, Plus, Edit, Trash2, CheckSquare, Type, File,
  ImageIcon, Volume2, Mic, Lightbulb
 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import InlineMathField from '../math/InlineMathField';
import { MathRenderer } from '../math/MathRenderer';
import { flipbookService } from '../../services/flipbookService';
import { firestoreService } from '../../services/firestoreService';
import { VoiceRecorder } from '../shared/VoiceRecorder';

const FileUploader = ({ onFileUploaded, maxFiles = 5, disabled = false, existingFiles = [] }) => {
  const [files, setFiles] = useState(existingFiles);
  const [uploading, setUploading] = useState(false);
  const [currentFile, setCurrentFile] = useState('');
  const fileInputRef = useRef(null);
  
  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    if (selectedFiles.length === 0) return;
    
    if (files.length + selectedFiles.length > maxFiles) {
      alert(`You can only upload up to ${maxFiles} files`);
      return;
    }
    
    setUploading(true);
    
    try {
      for (const file of selectedFiles) {
        setCurrentFile(file.name);
        
        const fileInfo = {
          name: file.name,
          type: file.type,
          size: file.size,
          file: file
        };
        
        setFiles(prevFiles => [...prevFiles, fileInfo]);
        
        if (onFileUploaded) {
          onFileUploaded(fileInfo);
        }
      }
    } catch (error) {
      console.error('Error processing files:', error);
    } finally {
      setUploading(false);
      setCurrentFile('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const removeFile = (index) => {
    const newFiles = [...files];
    const removedFile = newFiles.splice(index, 1)[0];
    setFiles(newFiles);
    
    if (onFileUploaded) {
      onFileUploaded(null, removedFile, true);
    }
  };
  
  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) {
      return <Image size={20} className="text-blue-500" />;
    } else if (fileType === 'application/pdf') {
      return <FileText size={20} className="text-red-500" />;
    } else {
      return <FileText size={20} className="text-gray-500" />;
    }
  };
  
  return (
    <div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        multiple={maxFiles > 1}
        accept="image/*,application/pdf"
      />
      
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => fileInputRef.current.click()}
          disabled={uploading || files.length >= maxFiles || disabled}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Upload size={18} className="mr-2" />
          Add Images/PDFs
        </button>
        
        {uploading && (
          <div className="flex items-center text-sm text-gray-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            Uploading {currentFile}...
          </div>
        )}
        
        {files.length > 0 && (
          <div className="mt-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Attached Files:</p>
            <div className="space-y-2">
              {files.map((file, index) => (
                <div 
                  key={index} 
                  className="flex items-center p-2 bg-gray-50 border border-gray-200 rounded-lg"
                >
                  {getFileIcon(file.type)}
                  <span className="ml-2 text-sm truncate flex-1">{file.name}</span>
                  <span className="text-xs text-gray-500 mx-2">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const PageResponseModal = ({ 
  bookId, 
  pageNumber, 
  bookletId,
  bookData,
  bookletTitle,
  bookOwnerId,
  user,
  onClose,
  onSubmitSuccess 
}) => {
  const [response, setResponse] = useState('');
  const [responseAttachments, setResponseAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [responseType, setResponseType] = useState('practice');

  const [questions, setQuestions] = useState([]);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [questionType, setQuestionType] = useState('text');
  const [questionText, setQuestionText] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [allowMultipleAnswers, setAllowMultipleAnswers] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  const [hintText, setHintText] = useState('');
  const [hintAudioUrl, setHintAudioUrl] = useState(null);
  const [showHintAfterSubmit, setShowHintAfterSubmit] = useState(false);
  const [showVoiceHintRecorder, setShowVoiceHintRecorder] = useState(false);
  const [viewedHints, setViewedHints] = useState({});
  const [questionMarks, setQuestionMarks] = useState(10);

  const { user: authUser } = useAuth();
  const isOwner = authUser?.uid === bookOwnerId;
  const isEditor = bookData?.flipbookSettings?.editors?.includes(authUser?.uid);
  const canAddModelAnswer = isOwner || isEditor;
  const allowedResponseTypes = bookData?.flipbookSettings?.allowedResponseTypes || ['practice', 'graded', 'model'];

  const modalRef = useRef(null);
  const dragRef = useRef({ offsetX: 0, offsetY: 0, isDragging: false });

  React.useEffect(() => {
    loadQuestions();
  }, [bookId, bookletId, pageNumber]);

  const loadQuestions = async () => {
    try {
      const loadedQuestions = await flipbookService.getPageQuestions(bookId, bookletId, pageNumber);
      setQuestions(loadedQuestions);
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.modal-header')) {
      dragRef.current.isDragging = true;
      dragRef.current.offsetX = e.clientX - modalRef.current.offsetLeft;
      dragRef.current.offsetY = e.clientY - modalRef.current.offsetTop;
    }
  };

  const handleMouseMove = (e) => {
    if (dragRef.current.isDragging && modalRef.current) {
      modalRef.current.style.left = `${e.clientX - dragRef.current.offsetX}px`;
      modalRef.current.style.top = `${e.clientY - dragRef.current.offsetY}px`;
    }
  };

  const handleMouseUp = () => {
    dragRef.current.isDragging = false;
  };

  const handleAttachmentUpdate = (fileInfo, removedFile, isRemoval) => {
    if (isRemoval && removedFile) {
      setResponseAttachments(prev => prev.filter(file => file.name !== removedFile.name));
      
      const imageRef = `![${removedFile.name}](${removedFile.url || 'pending'})`;
      setResponse(prev => prev.replace(imageRef, ''));
    } else if (fileInfo) {
      setResponseAttachments(prev => [...prev, fileInfo]);
      
      const imageRef = `\n![${fileInfo.name}](pending)\n`;
      setResponse(prev => prev + imageRef);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestionId(question.id);
    setQuestionType(question.questionType);
    setQuestionText(question.questionText);
    setHintText(question.hint || '');
    setHintAudioUrl(question.hintAudio || null);
    setShowHintAfterSubmit(question.showHintAfterSubmit || false);
    setQuestionMarks(question.marks || 10);
    
    if (question.questionType === 'multiple_choice') {
      setOptions(question.options || ['', '']);
      setAllowMultipleAnswers(question.allowMultipleAnswers || false);
    }
    setShowQuestionForm(true);
  };

  // Update handleCancelEdit function (around line 230)
  const handleCancelEdit = () => {
    setEditingQuestionId(null);
    setShowQuestionForm(false);
    setQuestionText('');
    setQuestionType('text');
    setOptions(['', '']);
    setAllowMultipleAnswers(false);
    setHintText('');
    setHintAudioUrl(null);
    setShowHintAfterSubmit(false);
    setShowVoiceHintRecorder(false);
    setQuestionMarks(10);
  };

  const handleSaveQuestion = async () => {
    if (!questionText.trim()) {
      alert('Please enter a question');
      return;
    }

    if (questionType === 'multiple_choice' && options.filter(o => o.trim()).length < 2) {
      alert('Please add at least 2 options');
      return;
    }

    try {
      const questionData = {
        pageNumber,
        questionType,
        questionText,
        createdBy: authUser.uid,
        hint: hintText.trim() || null,
        hintAudio: hintAudioUrl || null,
        showHintAfterSubmit: showHintAfterSubmit,
        marks: questionMarks || 10
      };

      if (questionType === 'multiple_choice') {
        questionData.options = options.filter(o => o.trim());
        questionData.allowMultipleAnswers = allowMultipleAnswers;
      }

      if (editingQuestionId) {
        await flipbookService.updatePageQuestion(editingQuestionId, questionData);
      } else {
        await flipbookService.createPageQuestion(bookId, bookletId, questionData);
      }
      
      handleCancelEdit();
      loadQuestions();
    } catch (error) {
      console.error('Error saving question:', error);
      alert('Failed to save question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    
    try {
      await flipbookService.deletePageQuestion(questionId);
      loadQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Failed to delete question');
    }
  };

  const addOption = () => {
    setOptions([...options, '']);
  };

  const removeOption = (index) => {
    if (options.length <= 2) return;
    setOptions(options.filter((_, i) => i !== index));
  };

  const updateOption = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handlePreview = () => {
    if (!response.trim() && responseAttachments.length === 0) {
      alert('Please enter a response or attach a file');
      return;
    }
    setShowPreview(true);
  };

  const handleBackToEdit = () => {
    setShowPreview(false);
  };

  const handleSubmit = async () => {
    if (submitting) return;

    if (!response.trim() && responseAttachments.length === 0) {
      alert('Please enter a response or attach a file');
      return;
    }

    try {
      setSubmitting(true);

      let finalContent = response;
      const uploadedFiles = [];

      // Upload all attachments
      for (const attachment of responseAttachments) {
        if (attachment.file) {
          const uploadResult = await flipbookService.uploadResponseFile(
            bookId, 
            bookletId, 
            pageNumber, 
            attachment.file
          );
          
          uploadedFiles.push({
            name: attachment.name,
            type: attachment.type,
            size: attachment.size,
            url: uploadResult.url,
            storagePath: uploadResult.storagePath
          });

          // Replace pending placeholder with actual URL
          finalContent = finalContent.replace(
            `![${attachment.name}](pending)`,
            `![${attachment.name}](${uploadResult.url})`
          );
        }
      }

      const responseData = {
        pageNumber,
        bookletId,
        bookletTitle: bookletTitle || 'Untitled Booklet',
        questionId: selectedQuestionId || null,
        userId: user?.uid || 'anonymous',
        userDisplayName: user?.displayName || 'Anonymous',
        userEmail: user?.email || null,
        bookOwnerId: bookOwnerId || null,
        content: finalContent,
        responseType: responseType,
      };

      // Only add attachments if there are files
      if (uploadedFiles.length > 0) {
        responseData.attachments = uploadedFiles;
      }

      await flipbookService.createPageResponse(bookId, bookletId, responseData);

      onSubmitSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error submitting response:', error);
      alert('Failed to submit response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-70 z-[100] flex items-center justify-center p-4"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <div 
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl"
        style={{ position: 'fixed', cursor: dragRef.current.isDragging ? 'grabbing' : 'default' }}
      >
        <div 
          className="modal-header flex items-center justify-between p-4 border-b border-gray-400 cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
        >
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Submit Response</h3>
            <p className="text-sm text-gray-600">{bookletTitle} - Page {pageNumber + 1}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
        {/* Question Management for Owners/Editors */}
        {canAddModelAnswer && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-900">Page Questions</h4>
              {!showQuestionForm && (
                <button
                  type="button"
                  onClick={() => setShowQuestionForm(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Question
                </button>
              )}
            </div>

            {/* Question Creation Form */}
            {showQuestionForm && (
              <div className="mb-4 p-4 bg-white rounded-lg border border-gray-300">
                <h5 className="text-sm font-semibold text-gray-900 mb-3">
                  {editingQuestionId ? 'Edit Question' : 'Create New Question'}
                </h5>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question Type
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setQuestionType('text')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                        questionType === 'text'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 text-gray-700 hover:border-blue-300'
                      }`}
                    >
                      <Type className="w-4 h-4" />
                      Text Question
                    </button>
                    <button
                      type="button"
                      onClick={() => setQuestionType('multiple_choice')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                        questionType === 'multiple_choice'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 text-gray-700 hover:border-blue-300'
                      }`}
                    >
                      <CheckSquare className="w-4 h-4" />
                      Multiple Choice
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question Text
                  </label>
                  <p className="text-xs text-gray-600 mb-2">
                    Use <span className="font-mono bg-gray-100 px-1 rounded">Ctrl+M</span> for math expressions
                  </p>
                  <InlineMathField
                    value={questionText}
                    onChange={setQuestionText}
                    placeholder="Enter your question... Use Ctrl+M for math expressions"
                    rows={3}
                  />
                </div>

                {questionType === 'multiple_choice' && (
                  <>
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Options
                      </label>
                      <p className="text-xs text-gray-600 mb-2">
                        Use <span className="font-mono bg-gray-100 px-1 rounded">Ctrl+M</span> for math in options
                      </p>
                      <div className="space-y-2">
                        {options.map((option, index) => (
                          <div key={index} className="flex gap-2 items-start">
                            <div className="flex-1">
                              <InlineMathField
                                value={option}
                                onChange={(value) => updateOption(index, value)}
                                placeholder={`Option ${index + 1} (Ctrl+M for math)`}
                                rows={1}
                              />
                            </div>
                            {options.length > 2 && (
                              <button
                                type="button"
                                onClick={() => removeOption(index)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={addOption}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Option
                      </button>
                    </div>

                    <label className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        checked={allowMultipleAnswers}
                        onChange={(e) => setAllowMultipleAnswers(e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Allow multiple answers</span>
                    </label>
                  </>
                )}

                {/* Question Marks */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question Marks
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={questionMarks}
                      onChange={(e) => setQuestionMarks(Math.max(1, Math.min(100, parseInt(e.target.value) || 10)))}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <span className="text-sm text-gray-600">marks</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Total marks allocated to this question</p>
                </div>

                {/* Hints Section */}
                <div className="mb-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="w-4 h-4 text-yellow-500" />
                    <label className="block text-sm font-semibold text-purple-800">
                      Question Hints (Optional)
                    </label>
                  </div>

                  {/* Text Hint */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Text Hint
                    </label>
                    <InlineMathField
                      value={hintText}
                      onChange={setHintText}
                      placeholder="Provide a helpful hint... Use Ctrl+M for math expressions"
                      rows={2}
                    />
                  </div>

                  {/* Audio Hint */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-2 flex items-center gap-2">
                      <Volume2 className="w-3 h-3" />
                      Audio Hint
                    </label>
                    
                    {!hintAudioUrl && !showVoiceHintRecorder && (
                      <button
                        type="button"
                        onClick={() => setShowVoiceHintRecorder(true)}
                        className="w-full py-2 px-3 border border-dashed border-purple-600 rounded-lg text-sm text-gray-600 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-all flex items-center justify-center gap-2"
                      >
                        <Mic className="w-4 h-4" />
                        Record Audio Hint
                      </button>
                    )}

                    {showVoiceHintRecorder && !hintAudioUrl && (
                      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <VoiceRecorder
                          level="question"
                          onSave={async (audioData) => {
                            try {
                              const storagePath = `pb-${bookId}/flipbooks/${bookletId}/hints/${Date.now()}_hint.webm`;
                              const uploadResult = await firestoreService.uploadFile(audioData.blob, storagePath);
                              setHintAudioUrl(uploadResult.url);
                              setShowVoiceHintRecorder(false);
                            } catch (error) {
                              console.error('Error uploading hint audio:', error);
                              alert('Failed to upload audio hint');
                            }
                          }}
                          onCancel={() => setShowVoiceHintRecorder(false)}
                        />
                      </div>
                    )}

                    {hintAudioUrl && (
                      <div className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="flex-1 flex items-center gap-2">
                          <Volume2 className="w-4 h-4 text-purple-600" />
                          <span className="text-sm text-purple-700 font-medium">Audio hint recorded</span>
                        </div>
                        <audio controls src={hintAudioUrl} className="h-8 flex-1 max-w-xs" />
                        <button
                          type="button"
                          onClick={() => {
                            setHintAudioUrl(null);
                            setShowVoiceHintRecorder(true);
                          }}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remove audio hint"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Hint Visibility Control */}
                  {(hintText.trim() || hintAudioUrl) && (
                    <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="showHintAfterSubmit"
                        checked={showHintAfterSubmit}
                        onChange={(e) => setShowHintAfterSubmit(e.target.checked)}
                        className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                      />
                      <label htmlFor="showHintAfterSubmit" className="text-sm text-gray-700 cursor-pointer flex-1">
                        Only show hint <strong>after</strong> student submits their answer
                      </label>
                      {!showHintAfterSubmit && (
                        <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded">
                          Visible before
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveQuestion}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingQuestionId ? 'Update Question' : 'Save Question'}
                  </button>
                </div>
              </div>
            )}

            {/* Display Existing Questions for Editors */}
            {questions.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-600 uppercase">
                    Questions ({questions.length})
                  </p>
                  <p className="text-xs text-blue-600">Click to link your model answer</p>
                </div>
                {questions.map((q, index) => (
                  <div 
                    key={q.id}
                    onClick={() => setSelectedQuestionId(q.id)}
                    className={`p-3 bg-white rounded-lg border-2 transition-all cursor-pointer ${
                      selectedQuestionId === q.id
                        ? 'border-blue-500 shadow-md ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-blue-400 hover:shadow-sm'
                    }`}
                  >
                    {/* Selected indicator */}
                    {selectedQuestionId === q.id && (
                      <div className="flex items-center gap-2 mb-2 text-blue-600">
                        <CheckSquare className="w-4 h-4 fill-current" />
                        <span className="text-xs font-semibold uppercase">Selected for Model Answer</span>
                      </div>
                    )}

                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {q.questionType === 'text' ? (
                          <Type className="w-4 h-4 text-gray-500" />
                        ) : (
                          <CheckSquare className="w-4 h-4 text-gray-500" />
                        )}
                        <span className="text-xs text-gray-500 uppercase">
                          Question {index + 1}
                        </span>
                        {(q.hint || q.hintAudio) && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Lightbulb className="w-3 h-3" />
                            Hint
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {q.createdBy === authUser?.uid && (
                          <button
                            onClick={() => handleEditQuestion(q)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Edit question"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteQuestion(q.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete question"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-900 font-medium mb-2">
                      <MathRenderer content={q.questionText} />
                    </div>

                    {q.questionType === 'multiple_choice' && q.options && (
                      <div className="space-y-1 mb-2">
                        {q.options.map((option, idx) => (
                          <div key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                            <span className="w-5 h-5 flex items-center justify-center bg-gray-100 rounded text-gray-500 flex-shrink-0">
                              {String.fromCharCode(65 + idx)}
                            </span>
                            <span className="flex-1">
                              <MathRenderer content={option} />
                            </span>
                          </div>
                        ))}
                        {q.allowMultipleAnswers && (
                          <p className="text-xs text-blue-600 italic mt-1">Multiple answers allowed</p>
                        )}
                      </div>
                    )}

                    {/* Display hint info for editors */}
                    {(q.hint || q.hintAudio) && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="text-xs text-gray-600 flex items-start gap-2">
                          <Lightbulb className="w-3 h-3 text-yellow-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            {q.hint && (
                              <div className="mb-1">
                                <span className="font-medium">Hint:</span> <MathRenderer content={q.hint} />
                              </div>
                            )}
                            {q.hintAudio && (
                              <div className="flex items-center gap-2 mb-1">
                                <Volume2 className="w-3 h-3 text-purple-600" />
                                <span className="font-medium">Audio hint available</span>
                              </div>
                            )}
                            <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                              q.showHintAfterSubmit 
                                ? 'bg-orange-100 text-orange-700' 
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {q.showHintAfterSubmit ? 'After submission' : 'Before submission'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Clear selection button */}
                {selectedQuestionId && (
                  <button
                    onClick={() => setSelectedQuestionId(null)}
                    className="w-full py-2 px-4 bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Clear Question Selection
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Display Questions for Regular Users */}
        {!canAddModelAnswer && questions.length > 0 && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Questions</h4>
            <p className="text-xs text-gray-600 mb-3">Click a question to link your response to it</p>
            <div className="space-y-3">
              {questions.map((q, index) => (
                <div 
                  key={q.id}
                  onClick={() => setSelectedQuestionId(q.id)}
                  className={`bg-white p-4 rounded-lg border-2 transition-all cursor-pointer ${
                    selectedQuestionId === q.id
                      ? 'border-blue-500 shadow-md ring-2 ring-blue-200'
                      : 'border-blue-200 hover:border-blue-400 hover:shadow-sm'
                  }`}
                >
                  {/* Selected indicator */}
                  {selectedQuestionId === q.id && (
                    <div className="flex items-center gap-2 mb-2 text-blue-600">
                      <CheckSquare className="w-4 h-4 fill-current" />
                      <span className="text-xs font-semibold">Answer this question</span>
                    </div>
                  )}

                  <div className="text-sm font-medium text-gray-900 mb-3">
                    <span>{index + 1}. </span>
                    <MathRenderer content={q.questionText} />
                  </div>

                    {/* Show hints based on visibility settings */}
                    {(q.hint || q.hintAudio) && !q.showHintAfterSubmit && (
                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start gap-2 mb-2">
                          <Lightbulb className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                          <span className="text-xs font-semibold text-yellow-800 uppercase">Hint Available</span>
                        </div>
                        
                        {!viewedHints[q.id] ? (
                          <button
                            onClick={() => setViewedHints(prev => ({ ...prev, [q.id]: true }))}
                            className="text-sm text-yellow-700 hover:text-yellow-800 font-medium underline"
                          >
                            Click to view hint
                          </button>
                        ) : (
                          <div className="space-y-2">
                            {q.hint && (
                              <div className="text-sm text-gray-700">
                                <MathRenderer content={q.hint} />
                              </div>
                            )}
                            {q.hintAudio && (
                              <div className="flex items-center gap-2">
                                <Volume2 className="w-4 h-4 text-purple-600" />
                                <audio controls src={q.hintAudio} className="h-8 flex-1" />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {q.questionType === 'multiple_choice' && q.options && (
                      <div className="space-y-2">
                        {q.options.map((option, idx) => (
                          <label key={idx} className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                            <input
                              type={q.allowMultipleAnswers ? 'checkbox' : 'radio'}
                              name={`question-${q.id}`}
                              className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0"
                            />
                            <span className="text-sm text-gray-700 flex-1">
                              <MathRenderer content={option} />
                            </span>
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Post-submission hint message */}
                    {(q.hint || q.hintAudio) && q.showHintAfterSubmit && (
                      <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-center gap-2">
                        <Lightbulb className="w-3 h-3 flex-shrink-0" />
                        <span>Hint will be available after you submit your response</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Clear selection button */}
              {selectedQuestionId && (
                <button
                  onClick={() => setSelectedQuestionId(null)}
                  className="mt-3 w-full py-2 px-4 bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Clear Question Selection
                </button>
              )}
            </div>
          )}

        {/* Response Type Selection */}
        {!showPreview ? (
            <>
              {/* Response Type Selection */}
              {allowedResponseTypes.length > 1 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Response Type
                  </label>
                  <div className="flex gap-2">
                    {allowedResponseTypes.includes('practice') && (
                      <button
                        type="button"
                        onClick={() => setResponseType('practice')}
                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                          responseType === 'practice'
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-gray-300 text-gray-700 hover:border-blue-300'
                        }`}
                      >
                        Practice Response
                      </button>
                    )}
                    {allowedResponseTypes.includes('graded') && (
                      <button
                        type="button"
                        onClick={() => setResponseType('graded')}
                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                          responseType === 'graded'
                            ? 'border-purple-600 bg-purple-50 text-purple-700'
                            : 'border-gray-300 text-gray-700 hover:border-purple-300'
                        }`}
                      >
                        Graded Response
                      </button>
                    )}
                    {allowedResponseTypes.includes('model') && canAddModelAnswer && (
                      <button
                        type="button"
                        onClick={() => setResponseType('model')}
                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                          responseType === 'model'
                            ? 'border-green-600 bg-green-50 text-green-700'
                            : 'border-gray-300 text-gray-700 hover:border-green-300'
                        }`}
                      >
                        Model Answer
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Selected Question Indicator */}
              {selectedQuestionId && questions.length > 0 && (
                <div className="mb-4 p-3 bg-blue-100 border border-blue-300 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckSquare className="w-4 h-4 text-blue-600 fill-current" />
                        <span className="text-xs font-semibold text-blue-800 uppercase">Responding to Question:</span>
                      </div>
                      <div className="text-sm text-gray-900">
                        <MathRenderer content={questions.find(q => q.id === selectedQuestionId)?.questionText || ''} />
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedQuestionId(null)}
                      className="p-1 text-blue-600 hover:bg-blue-200 rounded transition-colors flex-shrink-0"
                      title="Clear selection"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Response
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  Type your answer below. Use <span className="font-mono bg-gray-100 px-1 rounded">Ctrl+M</span> to insert mathematical expressions.
                </p>
                <InlineMathField
                  value={response}
                  onChange={setResponse}
                  placeholder="Enter your response here... Use Ctrl+M for math expressions"
                  rows={6}
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attachments (Optional)
                </label>
                <FileUploader
                  onFileUploaded={handleAttachmentUpdate}
                  maxFiles={5}
                  existingFiles={responseAttachments}
                  disabled={submitting}
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePreview}
                  disabled={!user || (!response.trim() && responseAttachments.length === 0)}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  Preview Response
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Preview Mode */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Response Preview</h4>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    responseType === 'practice' 
                      ? 'bg-blue-100 text-blue-700'
                      : responseType === 'graded'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {responseType === 'practice' ? 'Practice Response' : 
                     responseType === 'graded' ? 'Graded Response' : 'Model Answer'}
                  </span>
                </div>

                {/* Rendered Response */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Your Response:
                  </label>
                  <div className="prose prose-sm max-w-none bg-white p-4 rounded border border-gray-200">
                    <MathRenderer content={response} />
                  </div>
                </div>

                {/* Attachments Preview */}
                {responseAttachments.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments:
                    </label>
                    <div className="space-y-2">
                      {responseAttachments.map((file, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          {file.type?.startsWith('image/') ? (
                            <ImageIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                          ) : file.type?.includes('pdf') ? (
                            <FileText className="h-5 w-5 text-red-600 flex-shrink-0" />
                          ) : (
                            <File className="h-5 w-5 text-gray-600 flex-shrink-0" />
                          )}
                          <span className="text-sm text-gray-700 flex-1 truncate">{file.name}</span>
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            {(file.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Please review your response carefully. Once submitted, {responseType === 'practice' ? 'you can practice multiple times' : responseType === 'graded' ? 'it will be sent for grading' : 'it will be saved as a model answer'}.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={handleBackToEdit}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                  disabled={submitting}
                >
                  <Edit className="w-4 h-4" />
                  Back to Edit
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    'Submit Response'
                  )}
                </button>
              </div>
            </>
          )}
      </div>
      </div>
    </div>
  );
};